/**
 * Iter-327 — Universal Payment Link (guest checkout page).
 *
 * Public, un-authenticated page reached via /pay/:token. Renders the
 * invoice summary and lets the recipient pay full or partial via Stripe
 * Checkout. Also polls for status after Stripe redirects back with
 * ?session_id so the "Payment received" state renders without a manual
 * refresh.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { CheckCircle2, ShieldCheck, Loader2, Clock, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function PayInvoice() {
  const { token } = useParams();
  const [search, setSearch] = useSearchParams();
  const returnSession = search.get('session_id');
  const wasCancelled = search.get('cancelled') === '1';

  const [invoice, setInvoice] = useState(null);
  const [status, setStatus] = useState('loading');   // loading|ready|paid|expired|disabled|voided|not_found
  const [errorMsg, setErrorMsg] = useState('');
  const [mode, setMode] = useState('full');          // 'full' | 'custom'
  const [customAmount, setCustomAmount] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pollingReceipt, setPollingReceipt] = useState(!!returnSession);
  const [receipt, setReceipt] = useState(null);

  const loadInvoice = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/invoices/${token}`);
      setInvoice(res.data);
      setStatus('ready');
    } catch (e) {
      const s = e.response?.status;
      const d = e.response?.data?.detail;
      if (s === 404) { setStatus('not_found'); setErrorMsg('This payment link does not exist.'); }
      else if (s === 410 && d && typeof d === 'object') { setStatus(d.status || 'disabled'); setErrorMsg(d.message || 'Payment link no longer available.'); }
      else { setStatus('not_found'); setErrorMsg('Unable to load invoice.'); }
    }
  }, [token]);

  useEffect(() => { loadInvoice(); }, [loadInvoice]);

  // Poll on return from Stripe until the tx flips to paid/failed/expired.
  useEffect(() => {
    if (!returnSession) return;
    let cancelled = false;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const res = await axios.get(`${API}/invoicing/checkout/status/${returnSession}`);
        if (cancelled) return;
        setReceipt(res.data);
        if (['paid', 'failed', 'expired'].includes((res.data.payment_status || '').toLowerCase())) {
          setPollingReceipt(false);
          // Re-fetch invoice for updated balance
          await loadInvoice();
          if ((res.data.payment_status || '').toLowerCase() === 'paid') {
            toast.success('Payment received — thank you!');
          }
          return;
        }
      } catch (_) { /* ignore — will retry */ }
      if (tries < 20) setTimeout(tick, 1500);
      else setPollingReceipt(false);
    };
    tick();
    return () => { cancelled = true; };
  }, [returnSession, loadInvoice]);

  const remaining = Number(invoice?.amount_remaining || 0);
  const feeHint = invoice?.pass_fees ? Number(invoice?.fee_hint || 0) : 0;
  const paidAmount = Number(invoice?.amount_paid || 0);
  const totalAmount = Number(invoice?.total_amount || 0);
  const hasPrior = paidAmount > 0.01;

  const amountToCharge = useMemo(() => {
    if (mode === 'full') return remaining;
    const n = parseFloat(customAmount);
    return isNaN(n) || n <= 0 ? 0 : Math.min(n, remaining);
  }, [mode, customAmount, remaining]);

  const submit = async () => {
    if (amountToCharge <= 0) { toast.error('Enter a valid amount'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/invoices/${token}/checkout`, {
        amount: amountToCharge,
        guest_email: guestEmail || null,
        guest_name: guestName || null,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Could not start checkout — please try again.');
      setSubmitting(false);
    }
  };

  // ── Render states ─────────────────────────────────────────────
  if (status === 'loading') {
    return <FullscreenState icon={<Loader2 className="h-8 w-8 animate-spin text-teal-600" />} title="Loading your invoice…" />;
  }
  if (status === 'not_found') {
    return <FullscreenState icon={<XCircle className="h-10 w-10 text-red-500" />} title="Payment link not found" subtitle={errorMsg} />;
  }
  if (status === 'paid') {
    return <FullscreenState icon={<CheckCircle2 className="h-10 w-10 text-emerald-500" />} title="This invoice is fully paid" subtitle="Thanks — no further action needed." />;
  }
  if (status === 'expired') {
    return <FullscreenState icon={<Clock className="h-10 w-10 text-amber-500" />} title="Payment link has expired" subtitle="Please contact iEvntür for a fresh link." />;
  }
  if (status === 'disabled' || status === 'voided') {
    return <FullscreenState icon={<XCircle className="h-10 w-10 text-slate-500" />} title="Payment link unavailable" subtitle={errorMsg || 'This link has been disabled.'} />;
  }

  const paidSuccess = receipt && (receipt.payment_status || '').toLowerCase() === 'paid';

  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-slate-50 to-white text-slate-900 flex flex-col">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          {invoice?.branding?.brand_logo_url ? (
            <img src={invoice.branding.brand_logo_url} alt="" className="h-10 w-10 rounded object-cover" />
          ) : (
            <div className="h-10 w-10 rounded flex items-center justify-center font-bold text-white" style={{ backgroundColor: invoice?.branding?.brand_accent_color || '#0f766e' }}>
              {(invoice?.branding?.brand_name || 'iE').slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate">{invoice?.branding?.brand_name || 'iEvntür'}</div>
            <div className="text-xs text-slate-500">
              {invoice?.branding?.brand_footer_text || 'Secure payment · Powered by iEvntür · Stripe'}
            </div>
          </div>
        </div>
        {invoice?.branding?.brand_cover_url && (
          <div
            className="h-32 sm:h-40 bg-cover bg-center border-t"
            style={{ backgroundImage: `url(${invoice.branding.brand_cover_url})` }}
            aria-hidden="true"
          />
        )}
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-8">
        {wasCancelled && !returnSession && (
          <div className="mb-4 p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-sm" data-testid="pay-cancelled-banner">
            Payment cancelled. You can try again below.
          </div>
        )}
        {paidSuccess && (
          <div className="mb-4 p-4 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-900" data-testid="pay-success-banner">
            <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-5 w-5" /> Payment received</div>
            <div className="text-sm mt-1">Thank you! A receipt is on the way. {remaining > 0.01 ? `Remaining balance: ${fmt(remaining)}.` : 'Invoice paid in full.'}</div>
          </div>
        )}
        {pollingReceipt && (
          <div className="mb-4 p-3 rounded-md border border-teal-300 bg-teal-50 text-teal-900 text-sm flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Confirming payment with Stripe…
          </div>
        )}

        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-xl">
                  {invoice?.branding?.kind === 'vendor_booking' ? 'Booking Payment' : `Invoice ${invoice?.invoice_number}`}
                </CardTitle>
                <div className="text-sm text-slate-500 mt-1">
                  Issued by <span className="font-medium text-slate-800">{invoice?.branding?.brand_name || invoice?.biller_name}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wide text-slate-500">Amount due</div>
                <div
                  className="text-3xl font-bold"
                  style={{ color: invoice?.branding?.brand_accent_color || '#0f766e' }}
                  data-testid="pay-remaining"
                >
                  {fmt(remaining)}
                </div>
                {hasPrior && (
                  <div className="text-xs text-slate-500 mt-0.5">
                    {fmt(paidAmount)} of {fmt(totalAmount)} paid
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {invoice?.branding?.booking_summary && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Booking summary</div>
                <div className="whitespace-pre-line">{invoice.branding.booking_summary}</div>
              </div>
            )}
            {remaining > 0.01 && !paidSuccess && (
              <>
                <div>
                  <Label className="text-sm font-semibold">How much would you like to pay?</Label>
                  <RadioGroup value={mode} onValueChange={setMode} className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label className={`border rounded-md px-3 py-3 cursor-pointer ${mode==='full'?'border-teal-600 bg-teal-50':'border-slate-200'}`}>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="full" id="pay-full" data-testid="pay-mode-full" />
                        <span className="font-medium">Pay full balance</span>
                      </div>
                      <div className="text-sm text-slate-600 mt-1 pl-6">{fmt(remaining)}</div>
                    </label>
                    <label className={`border rounded-md px-3 py-3 cursor-pointer ${mode==='custom'?'border-teal-600 bg-teal-50':'border-slate-200'}`}>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="custom" id="pay-custom" data-testid="pay-mode-custom" />
                        <span className="font-medium">Custom / partial amount</span>
                      </div>
                      <div className="pl-6 mt-2">
                        <Input
                          type="number"
                          inputMode="decimal"
                          min="0.01"
                          step="0.01"
                          max={remaining}
                          disabled={mode !== 'custom'}
                          value={customAmount}
                          onChange={(e) => setCustomAmount(e.target.value)}
                          placeholder="0.00"
                          data-testid="pay-custom-amount"
                        />
                        <div className="text-xs text-slate-500 mt-1">Max {fmt(remaining)}</div>
                      </div>
                    </label>
                  </RadioGroup>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="guest-name" className="text-sm">Your name (optional)</Label>
                    <Input id="guest-name" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Jane Doe" data-testid="pay-guest-name" />
                  </div>
                  <div>
                    <Label htmlFor="guest-email" className="text-sm">Email for receipt (optional)</Label>
                    <Input id="guest-email" type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="you@example.com" data-testid="pay-guest-email" />
                  </div>
                </div>

                {invoice?.pass_fees && (
                  <div className="text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2 text-slate-600">
                    A processing fee of about {fmt(feeHint)} (2.9% + $0.30) will be added at checkout.
                  </div>
                )}

                <Button
                  onClick={submit}
                  disabled={submitting || amountToCharge <= 0}
                  className="w-full h-12 text-base"
                  data-testid="pay-submit-btn"
                >
                  {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</>) : (
                    <><ShieldCheck className="h-5 w-5 mr-2" /> Pay {fmt(amountToCharge)} securely</>
                  )}
                </Button>
                <div className="text-xs text-center text-slate-500">
                  You&apos;ll be redirected to Stripe&apos;s secure checkout. iEvntür never sees your card details.
                </div>
              </>
            )}

            {remaining <= 0.01 && (
              <div className="rounded-md border border-emerald-300 bg-emerald-50 text-emerald-900 p-4 text-sm flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" /> This invoice has been paid in full.
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-4 text-xs text-slate-500 space-y-3">
          {invoice?.branding?.cancellation_policy && (
            <div className="rounded-md border border-slate-200 bg-white p-3">
              <div className="font-semibold text-slate-800 text-sm mb-1">Cancellation policy</div>
              <div className="whitespace-pre-line text-slate-600">{invoice.branding.cancellation_policy}</div>
            </div>
          )}
          {(invoice?.branding?.brand_contact_email || invoice?.branding?.brand_contact_phone || invoice?.branding?.brand_website_url) && (
            <div className="rounded-md border border-slate-200 bg-white p-3">
              <div className="font-semibold text-slate-800 text-sm mb-1">Questions?</div>
              <div className="space-y-0.5">
                {invoice.branding.brand_contact_email && <div>Email: <a href={`mailto:${invoice.branding.brand_contact_email}`} className="underline">{invoice.branding.brand_contact_email}</a></div>}
                {invoice.branding.brand_contact_phone && <div>Phone: <a href={`tel:${invoice.branding.brand_contact_phone}`} className="underline">{invoice.branding.brand_contact_phone}</a></div>}
                {invoice.branding.brand_website_url && <div>Web: <a href={invoice.branding.brand_website_url} target="_blank" rel="noopener noreferrer" className="underline break-all">{invoice.branding.brand_website_url}</a></div>}
              </div>
            </div>
          )}
          <div className="text-center pt-2">
            {invoice?.branding?.kind === 'vendor_booking'
              ? 'Secure payments powered by iEvntür'
              : 'Payments processed securely by iEvntür via Stripe'}
          </div>
        </div>
      </main>
    </div>
  );
}

function FullscreenState({ icon, title, subtitle }) {
  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full text-center bg-white rounded-lg border border-slate-200 shadow-sm p-8">
        <div className="flex justify-center mb-3">{icon}</div>
        <div className="text-lg font-semibold">{title}</div>
        {subtitle && <div className="text-sm text-slate-600 mt-2">{subtitle}</div>}
      </div>
    </div>
  );
}
